import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { SettlementLineItemType } from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { snakeCase } from 'lodash-es';

import { SettlementLineItem } from '~hooks/useSettlementLineItems/models';
import { Settlement } from '~hooks/useSettlements/models';

import { EM_DASH, GridConfigType } from '../SettlementDetails/constants';
import { JoinedListCell, TruncatedCell } from '../SettlementDetails/JoinedListCell';

const REBILL_KEY = 'rebill';

// Helper function to format delta with signs and colors
const formatDelta = (delta: number, currencyFormatter: (value: number) => string) => {
  const formattedValue = currencyFormatter(Math.abs(delta));

  if (delta === 0) {
    return { formattedValue, sign: '', color: 'inherit' };
  }

  const sign = delta > 0 ? '+' : '-';
  const color = delta > 0 ? 'success.main' : 'error.main';

  return { formattedValue, sign, color };
};

export function RebillSettlementLineItems({
  lineItems,
  settlement,
  currencyFormatter,
  gridConfig,
}: {
  lineItems: SettlementLineItem[];
  settlement?: Settlement;
  currencyFormatter: (value: number) => string;
  gridConfig: GridConfigType;
}) {
  const theme = useTheme();

  return lineItems.map((lineItem) => {
    const uniqueKey =
      REBILL_KEY + lineItem.date.toISOString() + JSON.stringify(lineItem.ids);

    const newValue = lineItem.rebillTotalAmount ?? lineItem.totalAmount;
    const delta = newValue - lineItem.totalAmount;

    const rateValue = lineItem.rebillRateValue ?? lineItem.rateValue ?? 0;

    const uniqueJobIds = [
      ...new Set(lineItem.ids.map(({ friendlyJobId }) => friendlyJobId)),
    ];

    const rateOrUnitText = lineItem.rateTypes
      .map((type) => t(`rate_types.${snakeCase(type)}`))
      .join(' | ');
    const timeRangeText =
      settlement?.settlementLineItemType === SettlementLineItemType.JOB
        ? [lineItem.payStartTime?.format('h:mmA'), lineItem.payEndTime?.format('h:mmA')]
            .filter(Boolean)
            .join(' - ')
        : '';
    const rateAndTimeText = [rateOrUnitText, timeRangeText].filter(Boolean).join(' ');

    const numLoadsText = t('approvals.driver_pay.settlements.pdf.num_loads', {
      num: lineItem.loadsCount,
    });
    const quantitiesText = lineItem.quantities
      .map((qty) => `${qty.quantity} ${qty.unitOfMeasure}`)
      .join(' | ');
    const loadsAndQuantitiesText = [numLoadsText, quantitiesText]
      .filter(Boolean)
      .join(' , ');

    return (
      <Box
        key={uniqueKey}
        sx={{
          ...gridConfig,
          borderBottom: (theme) => `1px solid ${theme.semantic.border}`,
          backgroundColor: (theme) =>
            delta !== 0 ? theme.semantic.chipBrandBg : 'transparent',
        }}
      >
        <Box>
          <Typography variant="body2">
            {dayjs.tz(lineItem.date.toISOString()).format('DD-MMM-YY')}
          </Typography>
          <JoinedListCell
            items={uniqueJobIds}
            emptyValue={null}
            sx={{
              fontSize: '10px',
              color: theme.brandV2.colors.treadGray1,
            }}
          />
        </Box>

        <JoinedListCell items={lineItem.truckExternalIds} />

        <JoinedListCell items={lineItem.equipmentTypes} emptyValue="" />

        <Typography variant="body2">{lineItem.driver?.fullName || EM_DASH}</Typography>

        <Typography variant="body2">
          {lineItem.vendorJobAssignment?.vendorAccount?.name || EM_DASH}
        </Typography>

        <JoinedListCell items={lineItem.ticketNumbers} />

        <JoinedListCell items={lineItem.materials} emptyValue="" />

        <TruncatedCell text={rateAndTimeText} emptyValue="" />

        <TruncatedCell text={loadsAndQuantitiesText} emptyValue="" />

        <Typography variant="body2">{currencyFormatter(rateValue)}</Typography>

        <Typography variant="body2">{currencyFormatter(lineItem.totalAmount)}</Typography>

        <Typography variant="body2">{currencyFormatter(newValue)}</Typography>

        <Typography
          variant="body2"
          sx={{
            color: formatDelta(delta, currencyFormatter).color,
            fontWeight: 'medium',
          }}
        >
          {formatDelta(delta, currencyFormatter).sign}
          {formatDelta(delta, currencyFormatter).formattedValue}
        </Typography>
      </Box>
    );
  });
}
